import { useCallback, type SyntheticEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslate } from 'react-admin';
import { Box, Tab, Tabs, useTheme } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupIcon from '@mui/icons-material/Group';
import BuildIcon from '@mui/icons-material/Build';

import { OverviewDashboard } from './Overview';
import { SourcingDashboard } from './Sourcing';
import { ConversionDashboard } from './Conversion';
import { RetentionDashboard } from './Retention';
import { OperationsDashboard } from './Operations';

type DashboardTab = 'overview' | 'sourcing' | 'conversion' | 'retention' | 'operations';

const VALID_TABS: DashboardTab[] = ['overview', 'sourcing', 'conversion', 'retention', 'operations'];

const isValidTab = (value: string | null): value is DashboardTab =>
  value !== null && VALID_TABS.includes(value as DashboardTab);

export const DashboardsTabs = () => {
  const translate = useTranslate();
  const theme = useTheme();
  // The app is mounted under a hash router, so the active route already lives in
  // window.location.hash. We must NOT write window.location.hash or call
  // history.replaceState for tab state — that overwrites the router route and
  // navigates the app off the dashboard. Drive the active tab from a search
  // param instead: under a hash router it lands after the '?', preserving the
  // /job-application-dashboard route while staying deep-linkable.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: DashboardTab = isValidTab(tabParam) ? tabParam : 'overview';

  const navigateToTab = useCallback(
    (newValue: DashboardTab) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', newValue);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleChange = useCallback(
    (_event: SyntheticEvent, newValue: DashboardTab) => navigateToTab(newValue),
    [navigateToTab],
  );

  return (
    <Box>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper',
        }}
      >
        <Tabs value={tab} onChange={handleChange} variant="scrollable" scrollButtons="auto" sx={{ px: 2 }}>
          <Tab
            value="overview"
            icon={<DashboardIcon />}
            iconPosition="start"
            label={translate('resources.job-application-dashboard.dashboards.tabOverview')}
            sx={{ minHeight: 48 }}
          />
          <Tab
            value="sourcing"
            icon={<PeopleIcon />}
            iconPosition="start"
            label={translate('resources.job-application-dashboard.dashboards.tabSourcing')}
            sx={{ minHeight: 48 }}
          />
          <Tab
            value="conversion"
            icon={<TrendingUpIcon />}
            iconPosition="start"
            label={translate('resources.job-application-dashboard.dashboards.tabConversion')}
            sx={{ minHeight: 48 }}
          />
          <Tab
            value="retention"
            icon={<GroupIcon />}
            iconPosition="start"
            label={translate('resources.job-application-dashboard.dashboards.tabRetention')}
            sx={{ minHeight: 48 }}
          />
          <Tab
            value="operations"
            icon={<BuildIcon />}
            iconPosition="start"
            label={translate('resources.job-application-dashboard.dashboards.tabOperations')}
            sx={{ minHeight: 48 }}
          />
        </Tabs>
      </Box>

      {tab === 'overview' && <OverviewDashboard onNavigate={navigateToTab} />}
      {tab === 'sourcing' && <SourcingDashboard />}
      {tab === 'conversion' && <ConversionDashboard />}
      {tab === 'retention' && <RetentionDashboard />}
      {tab === 'operations' && <OperationsDashboard />}
    </Box>
  );
};

export { OverviewDashboard } from './Overview';
export { SourcingDashboard } from './Sourcing';
export { ConversionDashboard } from './Conversion';
export { RetentionDashboard } from './Retention';
export { OperationsDashboard } from './Operations';
